import { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Globe, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const BG_URL =
  "https://images.unsplash.com/photo-1758641682181-1a817db17df0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBmaW5hbmNlJTIwYnVpbGRpbmclMjBuaWdodHxlbnwwfHx8fDE3ODM5MDI1NDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, register } = useAuth();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });

  // Sanitised returnTo: internal paths only (must start with "/" but not "//") — prevents open redirects.
  const rawReturnTo = params.get("returnTo") || "";
  const returnTo = rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//") ? rawReturnTo : "";

  if (user) return <Navigate to={returnTo || "/"} replace />;

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(loginForm.email, loginForm.password);
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(regForm.name, regForm.email, regForm.password);
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left visual panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img src={BG_URL} alt="Financial district at night" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-black tracking-tighter text-xl text-white">
              SphereInvest<span className="text-primary"> AI</span>
            </span>
          </div>
          <div className="space-y-5 max-w-md">
            <div className="overline-label">AI Investment Research</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white leading-[1.05]">
              Understand why a stock looks attractive — or risky.
            </h1>
            <p className="text-gray-300 leading-relaxed">
              Real fundamentals, technical signals, peer valuations and AI-written research summaries across 70+
              US &amp; UK large caps. Analysis, never instructions.
            </p>
          </div>
          <p className="text-xs font-mono text-gray-400">Research tools only · Not financial advice</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 noise-bg">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-7 h-7 bg-primary flex items-center justify-center rounded-sm">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-black tracking-tighter text-lg text-white">
              SphereInvest<span className="text-primary"> AI</span>
            </span>
          </div>

          <div className="overline-label mb-2">Secure access</div>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-8">
            Sign in to the terminal
          </h2>

          {/* Google sign-in — shown for both the sign-in and create-account tabs */}
          <button
            type="button"
            data-testid="google-signin-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              // returnTo path (hash stripped — the auth provider uses the fragment for session_id)
              const returnPath = (returnTo || "/dashboard").split("#")[0];
              const redirectUrl = window.location.origin + returnPath;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-11 mb-5 flex items-center justify-center gap-3 rounded-sm bg-white text-[#3c4043] text-sm font-medium hover:bg-gray-100 transition-colors duration-150"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-3 mb-5" data-testid="auth-divider">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">or use email</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Tabs defaultValue={params.get("mode") === "register" ? "register" : "signin"}>
            <TabsList className="grid grid-cols-2 w-full rounded-sm bg-secondary mb-6">
              <TabsTrigger value="signin" className="rounded-sm" data-testid="signin-tab">
                Sign in
              </TabsTrigger>
              <TabsTrigger value="register" className="rounded-sm" data-testid="register-tab">
                Create account
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={doLogin} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="overline-label">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="rounded-sm bg-card border-border h-11 font-mono text-sm"
                    data-testid="login-email-input"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="overline-label">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    required
                    placeholder="••••••••"
                    className="rounded-sm bg-card border-border h-11 font-mono text-sm"
                    data-testid="login-password-input"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full h-11 rounded-sm bg-primary hover:bg-primary/85 text-white font-medium"
                  data-testid="login-submit-button"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-5" data-testid="register-form">
                <div className="space-y-2">
                  <Label htmlFor="reg-name" className="overline-label">Name</Label>
                  <Input
                    id="reg-name"
                    required
                    placeholder="Jane Analyst"
                    className="rounded-sm bg-card border-border h-11 text-sm"
                    data-testid="register-name-input"
                    value={regForm.name}
                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="overline-label">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="rounded-sm bg-card border-border h-11 font-mono text-sm"
                    data-testid="register-email-input"
                    value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="overline-label">Password (min 6 chars)</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    required
                    minLength={6}
                    placeholder="••••••••"
                    className="rounded-sm bg-card border-border h-11 font-mono text-sm"
                    data-testid="register-password-input"
                    value={regForm.password}
                    onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full h-11 rounded-sm bg-primary hover:bg-primary/85 text-white font-medium"
                  data-testid="register-submit-button"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-xs text-muted-foreground mt-8">
            This is AI-generated research, not financial advice.
          </p>
        </div>
      </div>
    </div>
  );
}
